import { useQuery } from "@tanstack/react-query";
import { Trophy, Medal, Crown, Clock, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const RANK_COLOR = ["text-amber-300", "text-slate-300", "text-orange-400"];

// The perk the month's #1 inviter wins — defined client-side (backend leaderboard carries no reward field).
const MONTHLY_REWARD = "3 months of BLOQ+ free";

function entryName(e) {
  return e.display_name || (e.username ? `@${e.username}` : null) || e.handle || e.name || "A BLOQ member";
}
function entryQualified(e) {
  return e.qualified ?? e.referrals_qualified ?? e.count ?? e.verified ?? 0;
}
function daysLeftInMonth() {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return Math.max(1, Math.ceil((end - now) / 86400000));
}

// "Top inviters this month" leaderboard off GET /referrals/leaderboard?period=month.
// Gracefully hides if the endpoint isn't available; shows an empty state until data exists.
export function ReferralLeaderboard() {
  const { data, isError } = useQuery({
    queryKey: ["referral-leaderboard", "month"],
    queryFn: () => api.referralLeaderboard("month"),
    retry: false,
    staleTime: 120000,
  });

  if (isError) return null;
  const top = data?.top || [];
  const me = data?.me;
  const daysLeft = daysLeftInMonth();
  const leaderQ = top[0] ? entryQualified(top[0]) : 0;
  const meRank = me?.rank ?? null;
  const meQ = me?.qualified ?? 0;
  const isLeader = meRank === 1;
  const gapToLead = Math.max(1, leaderQ - meQ + 1); // verified friends still needed to overtake #1

  return (
    <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="referral-leaderboard">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-bloq-muted">
          <Trophy className="h-3.5 w-3.5 text-amber-300" /> Top inviters this month
        </div>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-bold text-bloq-muted" data-testid="referral-leaderboard-countdown">
          <Clock className="h-3 w-3" /> {daysLeft}d left
        </span>
      </div>

      {/* Reward callout — makes topping the board worth chasing */}
      <div className="mt-3 flex items-center gap-3 overflow-hidden rounded-2xl border border-amber-400/35 bg-gradient-to-r from-amber-400/[0.14] to-bloq-mint/[0.06] p-3.5" data-testid="referral-leaderboard-reward">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/20 text-amber-300">
          <Crown className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-white">This month's #1 inviter wins {MONTHLY_REWARD}</div>
          <div className="text-xs text-bloq-muted">Plus the exclusive <span className="font-semibold text-amber-300">Top Connector</span> crown on your profile.</div>
        </div>
      </div>

      {top.length === 0 ? (
        <p className="mt-3 text-sm text-bloq-muted" data-testid="referral-leaderboard-empty">
          Be the first to top the board — invite friends and climb as they verify. The crown (and {MONTHLY_REWARD}) is up for grabs. 🏆
        </p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {top.slice(0, 10).map((e, i) => {
            const isTop = i === 0;
            return (
              <div
                key={e.id || e.username || i}
                className={cn(
                  "flex items-center gap-3 rounded-xl border px-3 py-2",
                  isTop ? "border-amber-400/40 bg-amber-400/[0.08]" : "border-white/8 bg-bloq-s2",
                )}
                data-testid={`referral-leaderboard-row-${i + 1}`}
              >
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center text-sm font-black", RANK_COLOR[i] || "text-bloq-muted")}>
                  {isTop ? <Crown className="h-4 w-4" /> : i < 3 ? <Medal className="h-4 w-4" /> : i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{entryName(e)}</span>
                {isTop && (
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300" data-testid="referral-leaderboard-leader-badge">
                    <Crown className="h-3 w-3" /> Leader
                  </span>
                )}
                <span className="shrink-0 rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[11px] font-bold text-bloq-mint">
                  {entryQualified(e)} verified
                </span>
              </div>
            );
          })}
        </div>
      )}

      {meRank ? (
        isLeader ? (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/[0.1] px-3 py-2.5 text-sm" data-testid="referral-leaderboard-me">
            <Crown className="h-4 w-4 shrink-0 text-amber-300" />
            <span className="font-semibold text-white">You're #1 — hold the lead for {daysLeft} more day{daysLeft === 1 ? "" : "s"} to claim {MONTHLY_REWARD} 🏆</span>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-bloq-purple/25 bg-bloq-purple/[0.06] px-3 py-2 text-sm" data-testid="referral-leaderboard-me">
            <span className="font-semibold text-white">You're #{meRank}</span>
            <span className="flex items-center gap-1 text-bloq-purple">
              <Gift className="h-3.5 w-3.5" /> {gapToLead} more to take the crown
            </span>
          </div>
        )
      ) : (
        <p className="mt-3 text-xs text-bloq-muted" data-testid="referral-leaderboard-me-unranked">
          Verify your first referral to join the board and chase {MONTHLY_REWARD}.
        </p>
      )}
    </div>
  );
}
